// Geometry engine for the /sewing-primitives "soft geometry" lab.
//
// The core idea of the page: every sewn 3D object is a flat pattern folded
// through 3D space, so each primitive here is described ONCE and rendered
// three ways — as flat pattern pieces on the cutting table (t = 0), as the
// assembled shape (t = 1), and as everything in between via positionsAt(t).
// Where the math allows it the fold is a true isometry (the cylinder wall
// really rolls, the cone sector really wraps — distances on the fabric are
// preserved); the sphere's gores and the lids simply blend flat → sewn.
//
// Units are centimetres. The cutting table is the y = 0 plane, y points up.
//
// A built model looks like:
//   {
//     positionsAt(t) -> [[x, y, z], ...]      // t in [0, 1]
//     faces:  [{ verts: [i, ...], piece }]     // piece keys into pieceTones
//     seams:  [{ id, label, color, edges: [[a, b], ...] }]
//     folds:  [[a, b], ...]                    // hinge lines, drawn dashed
//     pieces: [{ id, name, cut, outline, edges, folds2d, notes }]  // 2D draft
//     pieceTones: { pieceId: [hue, sat] }      // fabric tint per piece
//     insight: "one-line takeaway"
//   }
// pieces[].outline / edges use 2D pattern coords (x right, y down, cm);
// edges are { seam: seamId|null, pts: [[x, y], [x, y]] } so the pattern view
// can color each cut edge with the seam it belongs to — matching colors on
// the 3D shape show exactly which edges sew together.

const TAU = Math.PI * 2;

/* ============================================================
   Small helpers
   ============================================================ */

// Seam colors — assigned per seam so "same color = these edges sew together"
// holds in both the 3D view and the flat pattern.
export const SEAM_PALETTE = [
  "#f43f5e", // rose
  "#0ea5e9", // sky
  "#22c55e", // green
  "#a855f7", // purple
  "#f97316", // orange
  "#14b8a6", // teal
  "#eab308", // yellow
  "#ec4899", // pink
];

export const seamColor = (i) => SEAM_PALETTE[i % SEAM_PALETTE.length];

// Round for display: 31.41592 -> "31.4".
export const fmt = (x) =>
  (Math.round(x * 10) / 10).toLocaleString("en-US", {
    maximumFractionDigits: 1,
  });

const lerp = (a, b, t) => a + (b - a) * t;
const lerp3 = (a, b, t) => [
  lerp(a[0], b[0], t),
  lerp(a[1], b[1], t),
  lerp(a[2], b[2], t),
];

// Sample a circle (2D pattern coords) as a closed polygon.
const circleOutline = (r, steps = 48) => {
  const pts = [];
  for (let i = 0; i < steps; i++) {
    const a = (TAU * i) / steps;
    pts.push([r * Math.cos(a), r * Math.sin(a)]);
  }
  return pts;
};

// Turn a closed outline into per-segment edges all belonging to one seam.
const outlineEdges = (pts, seam) =>
  pts.map((p, i) => ({ seam, pts: [p, pts[(i + 1) % pts.length]] }));

// Registration tick marks for a circle piece: little dashes at the quarter
// points, pointing inward. Real patterns carry these so a curved edge can be
// pinned to its partner at matching points before easing the in-betweens.
const circleMarks = (r, tick = Math.max(0.6, r * 0.15)) =>
  [0, 1, 2, 3].map((k) => {
    const a = (TAU * k) / 4;
    return [
      [r * Math.cos(a), r * Math.sin(a)],
      [(r - tick) * Math.cos(a), (r - tick) * Math.sin(a)],
    ];
  });

/* ============================================================
   Box — six faces, one cross-shaped net, folded on hinges.
   ============================================================ */

const buildBox = ({ width: w, depth: d, height: h }) => {
  // Vertex layout (4 per face, hinge vertices duplicated on purpose so each
  // face is its own quad): bottom 0-3, front 4-7, back 8-11, left 12-15,
  // right 16-19, top 20-23.
  const positionsAt = (t) => {
    const th = (t * Math.PI) / 2; // every hinge folds 0° -> 90°
    const sin = Math.sin(th);
    const cos = Math.cos(th);
    const sin2 = Math.sin(2 * th);
    const cos2 = Math.cos(2 * th);
    // Top flap hinge point on the back flap's far edge, and the direction the
    // flap extends (it has folded twice: once with the back, once itself).
    const hinge = (x) => [x, h * sin, -d / 2 - h * cos];
    const dir = [0, sin2, -cos2];
    const top = (x, u) => {
      const p = hinge(x);
      return [p[0] + u * dir[0], p[1] + u * dir[1], p[2] + u * dir[2]];
    };
    return [
      // bottom (fixed on the table)
      [-w / 2, 0, -d / 2],
      [w / 2, 0, -d / 2],
      [w / 2, 0, d / 2],
      [-w / 2, 0, d / 2],
      // front flap, hinged at z = d/2
      [-w / 2, 0, d / 2],
      [w / 2, 0, d / 2],
      [w / 2, h * sin, d / 2 + h * cos],
      [-w / 2, h * sin, d / 2 + h * cos],
      // back flap, hinged at z = -d/2
      [-w / 2, 0, -d / 2],
      [w / 2, 0, -d / 2],
      [w / 2, h * sin, -d / 2 - h * cos],
      [-w / 2, h * sin, -d / 2 - h * cos],
      // left flap, hinged at x = -w/2
      [-w / 2, 0, -d / 2],
      [-w / 2, 0, d / 2],
      [-w / 2 - h * cos, h * sin, d / 2],
      [-w / 2 - h * cos, h * sin, -d / 2],
      // right flap, hinged at x = w/2
      [w / 2, 0, -d / 2],
      [w / 2, 0, d / 2],
      [w / 2 + h * cos, h * sin, d / 2],
      [w / 2 + h * cos, h * sin, -d / 2],
      // top flap, hinged to the back flap's far edge
      top(-w / 2, 0),
      top(w / 2, 0),
      top(w / 2, d),
      top(-w / 2, d),
    ];
  };

  const faces = [
    { verts: [0, 1, 2, 3], piece: "net" },
    { verts: [4, 5, 6, 7], piece: "net" },
    { verts: [8, 9, 10, 11], piece: "net" },
    { verts: [12, 13, 14, 15], piece: "net" },
    { verts: [16, 17, 18, 19], piece: "net" },
    { verts: [20, 21, 22, 23], piece: "net" },
  ];

  const seams = [
    { id: "fl", label: "front–left corner", edges: [[4, 7], [13, 14]] },
    { id: "fr", label: "front–right corner", edges: [[5, 6], [17, 18]] },
    { id: "bl", label: "back–left corner", edges: [[8, 11], [12, 15]] },
    { id: "br", label: "back–right corner", edges: [[9, 10], [16, 19]] },
    { id: "tl", label: "lid, left edge", edges: [[20, 23], [14, 15]] },
    { id: "tr", label: "lid, right edge", edges: [[21, 22], [18, 19]] },
    { id: "tf", label: "lid, front edge — leave most of this open for turning & stuffing", edges: [[22, 23], [6, 7]] },
  ].map((s, i) => ({ ...s, color: seamColor(i), order: i + 1, gap: s.id === "tf" }));

  const folds = [
    [2, 3],
    [0, 1],
    [0, 3],
    [1, 2],
    [10, 11],
  ];

  // 2D net (x right, y down; back is up the page). Walking the cross
  // clockwise gives every seam color exactly twice on the outline.
  const A = [-w / 2, -(d / 2 + h + d)];
  const B = [w / 2, -(d / 2 + h + d)];
  const C = [w / 2, -(d / 2 + h)];
  const D = [w / 2, -d / 2];
  const E = [w / 2 + h, -d / 2];
  const F = [w / 2 + h, d / 2];
  const G = [w / 2, d / 2];
  const H = [w / 2, d / 2 + h];
  const I = [-w / 2, d / 2 + h];
  const J = [-w / 2, d / 2];
  const K = [-(w / 2 + h), d / 2];
  const L = [-(w / 2 + h), -d / 2];
  const M = [-w / 2, -d / 2];
  const N = [-w / 2, -(d / 2 + h)];
  const netOutline = [A, B, C, D, E, F, G, H, I, J, K, L, M, N];
  const netEdges = [
    { seam: "tf", pts: [A, B] },
    { seam: "tr", pts: [B, C] },
    { seam: "br", pts: [C, D] },
    { seam: "br", pts: [D, E] },
    { seam: "tr", pts: [E, F] },
    { seam: "fr", pts: [F, G] },
    { seam: "fr", pts: [G, H] },
    { seam: "tf", pts: [H, I] },
    { seam: "fl", pts: [I, J] },
    { seam: "fl", pts: [J, K] },
    { seam: "tl", pts: [K, L] },
    { seam: "bl", pts: [L, M] },
    { seam: "bl", pts: [M, N] },
    { seam: "tl", pts: [N, A] },
  ];

  return {
    positionsAt,
    faces,
    seams,
    folds,
    pieces: [
      {
        id: "net",
        name: "box net",
        cut: 1,
        outline: netOutline,
        edges: netEdges,
        folds2d: [
          [M, D],
          [J, G],
          [M, J],
          [D, G],
          [N, C],
        ],
        notes: [
          `${fmt(w)} × ${fmt(d)} × ${fmt(h)} cm — every rectangle is one face`,
          "each seam color appears twice: those two edges meet when it folds",
        ],
      },
    ],
    pieceTones: { net: [45, 85] },
    insight:
      "All straight seams. A corner is just three seams agreeing to meet — stuffing will round it off unless the fabric fights back.",
  };
};

/* ============================================================
   Box, strategy 2 — wall strip + two lids.
   The four sides are ONE long strip that hinge-folds into a
   square tube, exactly like the cylinder wall rolls into a
   round one: a box is a square cylinder. Assembles lying on
   its side, like the cylinder does.
   ============================================================ */

const buildBoxStrip = ({ width: w, depth: d, height: h }) => {
  const P = 2 * (w + d); // strip length = perimeter of the cross-section
  const segs = [w, d, w, d];
  const gap = Math.max(1.5, Math.min(w, d) * 0.35);

  // Strip verts: stations 0..4 along the strip × rows z = ∓h/2.
  const stIdx = (st, row) => st * 2 + row;
  const lidBase = (which) => 10 + which * 4;
  const lidFlatCenterZ = (which) => (which === 0 ? 1 : -1) * (h / 2 + gap + d / 2);

  const positionsAt = (t) => {
    const th = (t * Math.PI) / 2; // every hinge folds 0° -> 90°
    // Walk the cross-section polyline in the x/y plane, turning at hinges.
    const cross = [[-P / 2, 0]];
    let px = -P / 2;
    let py = 0;
    let ang = 0;
    for (let i = 0; i < 4; i++) {
      px += segs[i] * Math.cos(ang);
      py += segs[i] * Math.sin(ang);
      cross.push([px, py]);
      ang += th;
    }
    const pts = [];
    cross.forEach(([cx, cy]) => {
      pts.push([cx, cy, -h / 2], [cx, cy, h / 2]);
    });
    // Lids: sewn positions are the square tube's end cross-sections
    // (corners of the assembled loop); flat they lie beside the strip.
    const sewnCorners = [
      [-P / 2, 0],
      [w - P / 2, 0],
      [w - P / 2, d],
      [-P / 2, d],
    ];
    for (let which = 0; which < 2; which++) {
      const zc = lidFlatCenterZ(which);
      const zSewn = (which === 0 ? 1 : -1) * (h / 2);
      const flatCorners = [
        [-w / 2, zc - d / 2],
        [w / 2, zc - d / 2],
        [w / 2, zc + d / 2],
        [-w / 2, zc + d / 2],
      ];
      flatCorners.forEach(([fx, fz], i) => {
        const flat = [fx, 0, fz];
        const sewn = [sewnCorners[i][0], sewnCorners[i][1], zSewn];
        pts.push(lerp3(flat, sewn, t));
      });
    }
    return pts;
  };

  const faces = [];
  for (let i = 0; i < 4; i++) {
    faces.push({
      verts: [stIdx(i, 0), stIdx(i + 1, 0), stIdx(i + 1, 1), stIdx(i, 1)],
      piece: "strip",
    });
  }
  for (let which = 0; which < 2; which++) {
    const b = lidBase(which);
    faces.push({ verts: [b, b + 1, b + 2, b + 3], piece: "lid" });
  }

  const rimEdges = [];
  for (let i = 0; i < 4; i++) {
    rimEdges.push([stIdx(i, 0), stIdx(i + 1, 0)]);
    rimEdges.push([stIdx(i, 1), stIdx(i + 1, 1)]);
  }
  for (let which = 0; which < 2; which++) {
    const b = lidBase(which);
    for (let k = 0; k < 4; k++) rimEdges.push([b + k, b + ((k + 1) % 4)]);
  }

  const seams = [
    {
      id: "loop",
      label: "loop seam — the strip's short ends meet (one seam makes all four walls)",
      color: seamColor(0),
      edges: [[stIdx(0, 0), stIdx(0, 1)], [stIdx(4, 0), stIdx(4, 1)]],
      order: 1,
    },
    {
      id: "rim",
      label: "rims — each lid sews around one long edge; leave one side of the second lid open",
      color: seamColor(1),
      edges: rimEdges,
      order: 2,
      gap: true,
    },
  ];

  const folds = [1, 2, 3].map((st) => [stIdx(st, 0), stIdx(st, 1)]);

  // 2D pieces.
  const stripOutline = [
    [-P / 2, -h / 2],
    [P / 2, -h / 2],
    [P / 2, h / 2],
    [-P / 2, h / 2],
  ];
  const hingeXs = [w, w + d, w + d + w].map((x) => x - P / 2);
  const tick = Math.max(0.6, h * 0.12);
  const lidOutline = [
    [-w / 2, -d / 2],
    [w / 2, -d / 2],
    [w / 2, d / 2],
    [-w / 2, d / 2],
  ];

  return {
    positionsAt,
    faces,
    seams,
    folds,
    pieces: [
      {
        id: "strip",
        name: "wall strip",
        cut: 1,
        outline: stripOutline,
        edges: [
          { seam: "rim", pts: [stripOutline[0], stripOutline[1]] },
          { seam: "loop", pts: [stripOutline[1], stripOutline[2]] },
          { seam: "rim", pts: [stripOutline[2], stripOutline[3]] },
          { seam: "loop", pts: [stripOutline[3], stripOutline[0]] },
        ],
        folds2d: hingeXs.map((x) => [
          [x, -h / 2],
          [x, h / 2],
        ]),
        marks: hingeXs.flatMap((x) => [
          [[x, -h / 2], [x, -h / 2 + tick]],
          [[x, h / 2], [x, h / 2 - tick]],
        ]),
        notes: [
          `length = 2(w + d) = ${fmt(P)} cm — the perimeter, same idea as the cylinder's 2πr`,
          `height = ${fmt(h)} cm; fold ticks mark where the lid corners must land`,
        ],
      },
      {
        id: "lid",
        name: "lid",
        cut: 2,
        outline: lidOutline,
        edges: outlineEdges(lidOutline, "rim"),
        folds2d: [],
        notes: [
          `${fmt(w)} × ${fmt(d)} cm`,
          "pin each corner to a fold tick on the strip, then sew edge by edge",
        ],
      },
    ],
    pieceTones: { strip: [45, 85], lid: [200, 75] },
    insight:
      "A box is a square cylinder: one strip = all four walls (2(w+d) long, like 2πr), two lids close it. Corners come from fold lines, so they're soft unless topstitched.",
  };
};

/* ============================================================
   Box, strategy 3 — six separate panels.
   Every edge is a cut edge, so every edge is a seam: the most
   sewing, the most control, the crispest corners.
   ============================================================ */

const buildBoxPanels = ({ width: w, depth: d, height: h }) => {
  const gap = Math.max(1.2, Math.min(w, d, h) * 0.3);

  // Each panel: 4 flat corners on the table and 4 sewn corners on the box.
  // Order matters: corner k lerps flat[k] -> sewn[k].
  const zF = d / 2 + gap; // front panel's near edge, flat
  const zT = zF + h + gap; // top panel's near edge, flat (parked past the front)
  const panels = [
    {
      piece: "cap", // bottom
      flat: [[-w / 2, 0, -d / 2], [w / 2, 0, -d / 2], [w / 2, 0, d / 2], [-w / 2, 0, d / 2]],
      sewn: [[-w / 2, 0, -d / 2], [w / 2, 0, -d / 2], [w / 2, 0, d / 2], [-w / 2, 0, d / 2]],
    },
    {
      piece: "cap", // top — flips up and over the front wall
      flat: [[-w / 2, 0, zT + d], [w / 2, 0, zT + d], [w / 2, 0, zT], [-w / 2, 0, zT]],
      sewn: [[-w / 2, h, -d / 2], [w / 2, h, -d / 2], [w / 2, h, d / 2], [-w / 2, h, d / 2]],
    },
    {
      piece: "wall", // front
      flat: [[-w / 2, 0, zF], [w / 2, 0, zF], [w / 2, 0, zF + h], [-w / 2, 0, zF + h]],
      sewn: [[-w / 2, 0, d / 2], [w / 2, 0, d / 2], [w / 2, h, d / 2], [-w / 2, h, d / 2]],
    },
    {
      piece: "wall", // back
      flat: [[-w / 2, 0, -zF], [w / 2, 0, -zF], [w / 2, 0, -zF - h], [-w / 2, 0, -zF - h]],
      sewn: [[-w / 2, 0, -d / 2], [w / 2, 0, -d / 2], [w / 2, h, -d / 2], [-w / 2, h, -d / 2]],
    },
    {
      piece: "side", // left
      flat: [[-w / 2 - gap, 0, -d / 2], [-w / 2 - gap, 0, d / 2], [-w / 2 - gap - h, 0, d / 2], [-w / 2 - gap - h, 0, -d / 2]],
      sewn: [[-w / 2, 0, -d / 2], [-w / 2, 0, d / 2], [-w / 2, h, d / 2], [-w / 2, h, -d / 2]],
    },
    {
      piece: "side", // right
      flat: [[w / 2 + gap, 0, -d / 2], [w / 2 + gap, 0, d / 2], [w / 2 + gap + h, 0, d / 2], [w / 2 + gap + h, 0, -d / 2]],
      sewn: [[w / 2, 0, -d / 2], [w / 2, 0, d / 2], [w / 2, h, d / 2], [w / 2, h, -d / 2]],
    },
  ];

  const positionsAt = (t) => {
    const pts = [];
    panels.forEach((p) => {
      p.flat.forEach((f, i) => pts.push(lerp3(f, p.sewn[i], t)));
    });
    return pts;
  };

  const faces = panels.map((p, k) => ({
    verts: [k * 4, k * 4 + 1, k * 4 + 2, k * 4 + 3],
    piece: p.piece,
  }));

  // Panel vert indices: bottom 0-3, top 4-7, front 8-11, back 12-15,
  // left 16-19, right 20-23. (front/back/left/right: [0,1]=bottom edge,
  // [2,3] or [1,2]=vertical edges per the corner ordering above.)
  const verticalEdges = [
    [8, 11], [9, 10], // front verticals (left, right)
    [12, 15], [13, 14], // back verticals
    [16, 19], [17, 18], // left panel verticals (back, front)
    [20, 23], [21, 22], // right panel verticals
  ];
  const rimEdgesP = [
    // bottom panel + walls' bottom edges
    [0, 1], [1, 2], [2, 3], [3, 0],
    [8, 9], [12, 13], [16, 17], [20, 21],
    // top panel + walls' top edges
    [4, 5], [5, 6], [6, 7], [7, 4],
    [10, 11], [14, 15], [18, 19], [22, 23],
  ];

  const seams = [
    {
      id: "vertical",
      label: "vertical corners × 4 — sew the four walls into a loop first",
      color: seamColor(0),
      edges: verticalEdges,
      order: 1,
    },
    {
      id: "rim",
      label: "rims — set in the bottom fully, then the top, leaving one edge open",
      color: seamColor(1),
      edges: rimEdgesP,
      order: 2,
      gap: true,
    },
  ];

  const rect = (a, b) => [
    [-a / 2, -b / 2],
    [a / 2, -b / 2],
    [a / 2, b / 2],
    [-a / 2, b / 2],
  ];
  const capOutline = rect(w, d);
  const wallOutline = rect(w, h);
  const sideOutline = rect(d, h);
  const wallEdges = (o) => [
    { seam: "rim", pts: [o[0], o[1]] },
    { seam: "vertical", pts: [o[1], o[2]] },
    { seam: "rim", pts: [o[2], o[3]] },
    { seam: "vertical", pts: [o[3], o[0]] },
  ];

  return {
    positionsAt,
    faces,
    seams,
    folds: [],
    pieces: [
      {
        id: "cap",
        name: "top / bottom",
        cut: 2,
        outline: capOutline,
        edges: outlineEdges(capOutline, "rim"),
        folds2d: [],
        notes: [`${fmt(w)} × ${fmt(d)} cm`],
      },
      {
        id: "wall",
        name: "front / back",
        cut: 2,
        outline: wallOutline,
        edges: wallEdges(wallOutline),
        folds2d: [],
        notes: [`${fmt(w)} × ${fmt(h)} cm`],
      },
      {
        id: "side",
        name: "sides",
        cut: 2,
        outline: sideOutline,
        edges: wallEdges(sideOutline),
        folds2d: [],
        notes: [`${fmt(d)} × ${fmt(h)} cm`],
      },
    ],
    pieceTones: { cap: [200, 75], wall: [45, 85], side: [150, 60] },
    insight:
      "No folds at all: every edge is a seam, so every edge comes out crisp. Twelve edges of pinning — the price of sharpness.",
  };
};

/* ============================================================
   Box, strategy 4 — the boxed-corner pillow.
   No box net is ever drafted: sew a plain pillow from two
   oversized rectangles, then pinch each corner flat and sew
   straight across. The pattern looks NOTHING like the shape —
   the 3D comes from a clever seam. (The corner model here is
   stylized: real fabric bunches slightly, which is why boxed
   corners look softly rounded.)
   ============================================================ */

const buildBoxPillow = ({ width: w, depth: d, height: h }) => {
  const W2 = w + h; // panel size: finished face + h/2 margin all around
  const D2 = d + h;
  const gapL = Math.max(1.5, h * 0.4);
  const chamfer = h * 0.14; // small corner bevel (stuffing rounds corners)
  const ear = h * 0.3; // how far the sewn-off dog-ear pokes out

  // Per panel: center quad, 4 margin quads, and per corner a bevel triangle
  // (fabric that wraps the corner) + a dog-ear triangle (the pinched-off
  // excess, sticking diagonally out at mid-height — trim or tuck it in real
  // life). Both panels mirror through the mid-height plane y = h/2.
  const CORNERS = [
    [1, 1],
    [-1, 1],
    [-1, -1],
    [1, -1],
  ];

  const buildPanel = (top, zOff) => {
    // Returns { flat: [...], sewn: [...] } position pairs, in a fixed order:
    // 4 center, 4×4 margins (front, back, left, right), 4×(3+3) corners.
    const yFace = top ? h : 0;
    const yMid = h / 2;
    const flat = [];
    const sewn = [];
    const push = (f, s) => {
      flat.push([f[0], 0, zOff + f[1]]);
      sewn.push(s);
    };
    // center
    CORNERS.forEach(([sx, sz]) => push([sx * (w / 2), sz * (d / 2)], [sx * (w / 2), yFace, sz * (d / 2)]));
    // margins: [inner-left, inner-right, outer-right, outer-left]
    const margin = (axis, sgn) => {
      // axis 'z': front/back margins (full width w); axis 'x': left/right (depth d)
      if (axis === "z") {
        push([-w / 2, sgn * (d / 2)], [-w / 2, yFace, sgn * (d / 2)]);
        push([w / 2, sgn * (d / 2)], [w / 2, yFace, sgn * (d / 2)]);
        push([w / 2, sgn * (d / 2 + h / 2)], [w / 2, yMid, sgn * (d / 2)]);
        push([-w / 2, sgn * (d / 2 + h / 2)], [-w / 2, yMid, sgn * (d / 2)]);
      } else {
        push([sgn * (w / 2), -d / 2], [sgn * (w / 2), yFace, -d / 2]);
        push([sgn * (w / 2), d / 2], [sgn * (w / 2), yFace, d / 2]);
        push([sgn * (w / 2 + h / 2), d / 2], [sgn * (w / 2), yMid, d / 2]);
        push([sgn * (w / 2 + h / 2), -d / 2], [sgn * (w / 2), yMid, -d / 2]);
      }
    };
    margin("z", 1);
    margin("z", -1);
    margin("x", -1);
    margin("x", 1);
    // corners: bevel tri then dog-ear tri
    CORNERS.forEach(([sx, sz]) => {
      const cx = sx * (w / 2);
      const cz = sz * (d / 2);
      // Pinch-seam ends: on the mid-height ring, inset by a small chamfer so
      // the corner reads as softly beveled (what stuffing does in real life).
      const pinchA = [cx, yMid, cz - sz * chamfer]; // on the x-wall side
      const pinchB = [cx - sx * chamfer, yMid, cz]; // on the z-wall side
      const bevelTop = [cx, yFace, cz];
      const earApex = [cx + sx * ear * 0.707, yMid, cz + sz * ear * 0.707];
      // bevel: face-corner point + the two pinch-seam ends
      push([cx, cz], bevelTop); // duplicate of center corner (own vertex)
      push([cx + sx * (h / 2), cz], pinchA);
      push([cx, cz + sz * (h / 2)], pinchB);
      // dog-ear: the same pinch ends + the panel's old pointy corner
      push([cx + sx * (h / 2), cz], pinchA);
      push([cx, cz + sz * (h / 2)], pinchB);
      push([cx + sx * (h / 2), cz + sz * (h / 2)], earApex);
    });
    return { flat, sewn };
  };

  const panelTop = buildPanel(true, -(D2 / 2 + gapL));
  const panelBot = buildPanel(false, D2 / 2 + gapL);
  const PANEL_VERTS = panelTop.flat.length; // 4 + 16 + 24 = 44

  const positionsAt = (t) => {
    const pts = [];
    [panelTop, panelBot].forEach((p) => {
      p.flat.forEach((f, i) => pts.push(lerp3(f, p.sewn[i], t)));
    });
    return pts;
  };

  const faces = [];
  const seamPerimeter = [];
  const seamPinch = [];
  [0, 1].forEach((which) => {
    const base = which * PANEL_VERTS;
    const piece = "panel";
    faces.push({ verts: [base, base + 1, base + 2, base + 3], piece });
    for (let m = 0; m < 4; m++) {
      const b = base + 4 + m * 4;
      faces.push({ verts: [b, b + 1, b + 2, b + 3], piece });
      seamPerimeter.push([b + 2, b + 3]); // margin outer edge = mid-height ring
    }
    for (let c = 0; c < 4; c++) {
      const b = base + 20 + c * 6;
      faces.push({ verts: [b, b + 1, b + 2], piece }); // bevel
      faces.push({ verts: [b + 3, b + 4, b + 5], piece }); // dog-ear
      seamPinch.push([b + 3, b + 4]); // the sewn-across pinch line
    }
  });

  const seams = [
    {
      id: "perimeter",
      label: "perimeter — sew the two panels together all the way around (leave a gap on one side)",
      color: seamColor(0),
      edges: seamPerimeter,
      order: 1,
      gap: true,
    },
    {
      id: "pinch",
      label: "corner pinches × 4 — flatten each corner and sew straight across; the box appears",
      color: seamColor(1),
      edges: seamPinch,
      order: 2,
    },
  ];

  // 2D pattern: one plain rectangle, cut 2. The pinch lines are drawn as
  // chamfer marks across the corners; the dashed inner rectangle shows where
  // the finished face will be.
  const panelOutline = [
    [-W2 / 2, -D2 / 2],
    [W2 / 2, -D2 / 2],
    [W2 / 2, D2 / 2],
    [-W2 / 2, D2 / 2],
  ];
  const pinch2d = CORNERS.map(([sx, sz]) => ({
    seam: "pinch",
    pts: [
      [sx * (W2 / 2 - h / 2), sz * (D2 / 2)],
      [sx * (W2 / 2), sz * (D2 / 2 - h / 2)],
    ],
  }));
  const inner = [
    [-w / 2, -d / 2],
    [w / 2, -d / 2],
    [w / 2, d / 2],
    [-w / 2, d / 2],
  ];

  return {
    positionsAt,
    faces,
    seams,
    folds: [],
    pieces: [
      {
        id: "panel",
        name: "panel",
        cut: 2,
        outline: panelOutline,
        edges: [...outlineEdges(panelOutline, "perimeter"), ...pinch2d],
        folds2d: inner.map((p, i) => [p, inner[(i + 1) % 4]]),
        notes: [
          `panel = (w + h) × (d + h) = ${fmt(W2)} × ${fmt(D2)} cm — just a big rectangle`,
          "pinch each sewn corner flat and sew across the marked line (it's h wide there)",
          "dashed rectangle = where the finished top face ends up",
        ],
      },
    ],
    pieceTones: { panel: [330, 70] },
    insight:
      "The pattern looks nothing like the shape: two flat rectangles become a box because of four corner pinches. This is how tote bags and cushions get their bottoms.",
  };
};

/* ============================================================
   Pyramid — square base with four triangle flaps leaning in.
   ============================================================ */

const buildPyramid = ({ base: b, height: h }) => {
  const m = Math.hypot(h, b / 2); // slant height of each triangular face
  const rhoF = Math.atan2(h, -b / 2); // full hinge rotation (obtuse angle)

  const positionsAt = (t) => {
    const th = t * rhoF;
    const sin = Math.sin(th);
    const cos = Math.cos(th);
    return [
      // base
      [-b / 2, 0, -b / 2],
      [b / 2, 0, -b / 2],
      [b / 2, 0, b / 2],
      [-b / 2, 0, b / 2],
      // front triangle (hinge z = b/2)
      [-b / 2, 0, b / 2],
      [b / 2, 0, b / 2],
      [0, m * sin, b / 2 + m * cos],
      // back triangle
      [-b / 2, 0, -b / 2],
      [b / 2, 0, -b / 2],
      [0, m * sin, -b / 2 - m * cos],
      // left triangle
      [-b / 2, 0, -b / 2],
      [-b / 2, 0, b / 2],
      [-b / 2 - m * cos, m * sin, 0],
      // right triangle
      [b / 2, 0, b / 2],
      [b / 2, 0, -b / 2],
      [b / 2 + m * cos, m * sin, 0],
    ];
  };

  const faces = [
    { verts: [0, 1, 2, 3], piece: "net" },
    { verts: [4, 5, 6], piece: "net" },
    { verts: [7, 8, 9], piece: "net" },
    { verts: [10, 11, 12], piece: "net" },
    { verts: [13, 14, 15], piece: "net" },
  ];

  const seams = [
    { id: "fl", label: "front–left edge", edges: [[4, 6], [11, 12]] },
    { id: "fr", label: "front–right edge", edges: [[5, 6], [13, 15]] },
    { id: "br", label: "back–right edge", edges: [[8, 9], [14, 15]] },
    { id: "bl", label: "back–left edge — sew only partway; the rest is the turning gap", edges: [[7, 9], [10, 12]] },
  ].map((s, i) => ({ ...s, color: seamColor(i), order: i + 1, gap: s.id === "bl" }));

  const folds = [
    [0, 1],
    [1, 2],
    [2, 3],
    [0, 3],
  ];

  // 2D net: square with a triangle on each edge (back is up the page).
  const NW = [-b / 2, -b / 2];
  const NE = [b / 2, -b / 2];
  const SE = [b / 2, b / 2];
  const SW = [-b / 2, b / 2];
  const apexN = [0, -(b / 2 + m)];
  const apexE = [b / 2 + m, 0];
  const apexS = [0, b / 2 + m];
  const apexW = [-(b / 2 + m), 0];
  const outline = [NW, apexN, NE, apexE, SE, apexS, SW, apexW];
  const edges = [
    { seam: "bl", pts: [NW, apexN] },
    { seam: "br", pts: [apexN, NE] },
    { seam: "br", pts: [NE, apexE] },
    { seam: "fr", pts: [apexE, SE] },
    { seam: "fr", pts: [SE, apexS] },
    { seam: "fl", pts: [apexS, SW] },
    { seam: "fl", pts: [SW, apexW] },
    { seam: "bl", pts: [apexW, NW] },
  ];

  return {
    positionsAt,
    faces,
    seams,
    folds,
    pieces: [
      {
        id: "net",
        name: "pyramid net",
        cut: 1,
        outline,
        edges,
        folds2d: [
          [NW, NE],
          [NE, SE],
          [SE, SW],
          [SW, NW],
        ],
        notes: [
          `triangle height = √(h² + (b/2)²) = ${fmt(m)} cm`,
          "shorter pyramid → triangles fold further past vertical",
        ],
      },
    ],
    pieceTones: { net: [150, 60] },
    insight:
      "Same trick as the box, but the flaps lean in until all four tips kiss. The triangle height isn't h — it's the slant, √(h² + (b/2)²).",
  };
};

/* ============================================================
   Cylinder — a rectangle that rolls up, plus two circle lids.
   The wall's fold is a true isometry: fabric distances are
   preserved at every t while it curls.
   ============================================================ */

const CYL_SEGS = 36;

// `segments` is a hidden tessellation override used by composites (many
// low-poly parts beat few high-poly ones in an SVG renderer).
const buildCylinder = ({ radius: r, height: h, segments }) => {
  const W = TAU * r; // wall width = circumference — the whole lesson
  const n = segments || CYL_SEGS;
  const gap = Math.max(1.5, r * 0.4); // flat-layout breathing room

  // Wall verts: columns 0..n, rows z = -h/2 and h/2. idx = col*2 + row.
  // Lid verts: center + n rim points each.
  const wallCount = (n + 1) * 2;
  const lidBase = (which) => wallCount + which * (n + 1); // center + n rim
  const flatLidCenter = (which) => [0, 0, (which === 0 ? 1 : -1) * (h / 2 + gap + r)];

  const positionsAt = (t) => {
    const pts = [];
    const phiTotal = Math.max(1e-4, t * TAU);
    const R = W / phiTotal; // roll radius: huge when flat, r when sewn
    for (let i = 0; i <= n; i++) {
      const x = -W / 2 + (W * i) / n;
      const phi = x / R;
      const px = R * Math.sin(phi);
      const py = R * (1 - Math.cos(phi));
      pts.push([px, py, -h / 2], [px, py, h / 2]);
    }
    for (let which = 0; which < 2; which++) {
      const sign = which === 0 ? 1 : -1;
      const flatC = flatLidCenter(which);
      const sewnC = [0, r, (sign * h) / 2];
      pts.push(lerp3(flatC, sewnC, t));
      for (let k = 0; k < n; k++) {
        const a = (TAU * k) / n;
        const flat = [flatC[0] + r * Math.cos(a), 0, flatC[2] + r * Math.sin(a)];
        const sewn = [r * Math.sin(a), r * (1 - Math.cos(a)), (sign * h) / 2];
        pts.push(lerp3(flat, sewn, t));
      }
    }
    return pts;
  };

  const wallIdx = (col, row) => col * 2 + row;
  const faces = [];
  for (let i = 0; i < n; i++) {
    faces.push({
      verts: [wallIdx(i, 0), wallIdx(i + 1, 0), wallIdx(i + 1, 1), wallIdx(i, 1)],
      piece: "wall",
    });
  }
  for (let which = 0; which < 2; which++) {
    const base = lidBase(which);
    for (let k = 0; k < n; k++) {
      faces.push({
        verts: [base, base + 1 + k, base + 1 + ((k + 1) % n)],
        piece: "lid",
      });
    }
  }

  const sideEdges = [
    [wallIdx(0, 0), wallIdx(0, 1)],
    [wallIdx(n, 0), wallIdx(n, 1)],
  ];
  const rimEdges = [];
  for (let i = 0; i < n; i++) {
    rimEdges.push([wallIdx(i, 0), wallIdx(i + 1, 0)]);
    rimEdges.push([wallIdx(i, 1), wallIdx(i + 1, 1)]);
  }
  for (let which = 0; which < 2; which++) {
    const base = lidBase(which);
    for (let k = 0; k < n; k++) {
      rimEdges.push([base + 1 + k, base + 1 + ((k + 1) % n)]);
    }
  }

  const seams = [
    { id: "side", label: "side seam — the wall's short edges meet", color: seamColor(0), edges: sideEdges, order: 1 },
    { id: "rim", label: "rims — each circle sews onto one long edge of the wall; leave a third of the second rim open", color: seamColor(1), edges: rimEdges, order: 2, gap: true },
  ];

  // 2D pattern pieces.
  const wallOutline = [
    [-W / 2, -h / 2],
    [W / 2, -h / 2],
    [W / 2, h / 2],
    [-W / 2, h / 2],
  ];
  const wallEdges = [
    { seam: "rim", pts: [wallOutline[0], wallOutline[1]] },
    { seam: "side", pts: [wallOutline[1], wallOutline[2]] },
    { seam: "rim", pts: [wallOutline[2], wallOutline[3]] },
    { seam: "side", pts: [wallOutline[3], wallOutline[0]] },
  ];
  const lidOutline = circleOutline(r);

  return {
    positionsAt,
    faces,
    seams,
    folds: [],
    pieces: [
      {
        id: "wall",
        name: "wall",
        cut: 1,
        outline: wallOutline,
        edges: wallEdges,
        folds2d: [],
        marks: [1, 2, 3].flatMap((k) => {
          const x = -W / 2 + (W * k) / 4;
          const tick = Math.max(0.6, h * 0.12);
          return [
            [[x, -h / 2], [x, -h / 2 + tick]],
            [[x, h / 2], [x, h / 2 - tick]],
          ];
        }),
        notes: [
          `width = 2πr = ${fmt(W)} cm — the circumference, not a guess`,
          `height = ${fmt(h)} cm`,
          "tick marks = registration: pin them to the circle's quarter marks, then ease between",
        ],
      },
      {
        id: "lid",
        name: "lid",
        cut: 2,
        outline: lidOutline,
        edges: outlineEdges(lidOutline, "rim"),
        folds2d: [],
        marks: circleMarks(r),
        notes: [
          `radius = ${fmt(r)} cm`,
          `edge length = 2πr = ${fmt(W)} cm — must match the wall exactly`,
        ],
      },
    ],
    pieceTones: { wall: [45, 85], lid: [200, 75] },
    insight:
      "The wall's width is not designed, it's computed: 2πr. If the circle's edge and the wall's edge aren't the same length, the fabric will pucker or gape.",
  };
};

/* ============================================================
   Cone — a pizza-slice of a bigger circle wraps into the cone;
   the wrap is a true isometry at every t.
   ============================================================ */

const CONE_SEGS = 48;

const buildCone = ({ radius: r, height: h }) => {
  const s = Math.hypot(r, h); // slant length = radius of the flat sector
  const phi0 = (TAU * r) / s; // sector angle: 360° shrunk by r/s
  const m = CONE_SEGS;
  const gap = Math.max(1.5, r * 0.4);

  // Verts: apex (0), sector rim 1..m+1, then base circle center + rim.
  const baseCenterFlat = [0, 0, s + gap + r];
  const baseIdx = m + 2;

  const positionsAt = (t) => {
    const sinA = lerp(1, r / s, t); // half-angle of the partial cone
    const cosA = Math.sqrt(Math.max(0, 1 - sinA * sinA));
    const pts = [[0, cosA * s, 0]]; // apex rises as the sector wraps
    for (let i = 0; i <= m; i++) {
      const theta = -phi0 / 2 + (phi0 * i) / m;
      const psi = theta / sinA; // flat angle -> azimuth around the cone
      pts.push([
        s * sinA * Math.sin(psi),
        cosA * 0, // rim stays on the table (rho = s ⇒ height cosA·(s−s) = 0)
        s * sinA * Math.cos(psi),
      ]);
    }
    // Base circle: slides underneath and lies flat (tiny offset avoids
    // z-fighting with the cone rim once assembled).
    const sewnC = [0, -0.08, 0];
    pts.push(lerp3(baseCenterFlat, sewnC, t));
    for (let k = 0; k < m; k++) {
      const a = (TAU * k) / m;
      const flat = [
        baseCenterFlat[0] + r * Math.cos(a),
        0,
        baseCenterFlat[2] + r * Math.sin(a),
      ];
      const sewn = [r * Math.cos(a), -0.08, r * Math.sin(a)];
      pts.push(lerp3(flat, sewn, t));
    }
    return pts;
  };

  const faces = [];
  for (let i = 0; i < m; i++) {
    faces.push({ verts: [0, 1 + i, 2 + i], piece: "sector" });
  }
  for (let k = 0; k < m; k++) {
    faces.push({
      verts: [baseIdx, baseIdx + 1 + k, baseIdx + 1 + ((k + 1) % m)],
      piece: "base",
    });
  }

  const baseEdges = [];
  for (let i = 0; i < m; i++) baseEdges.push([1 + i, 2 + i]);
  for (let k = 0; k < m; k++) {
    baseEdges.push([baseIdx + 1 + k, baseIdx + 1 + ((k + 1) % m)]);
  }

  const seams = [
    { id: "side", label: "side seam — the sector's two straight edges meet", color: seamColor(0), edges: [[0, 1], [0, m + 1]], order: 1 },
    { id: "base", label: "base — the arc sews onto the circle; leave a third open for turning", color: seamColor(1), edges: baseEdges, order: 2, gap: true },
  ];

  // 2D sector: apex at origin, fanning down the page.
  const sectorPt = (rho, theta) => [rho * Math.sin(theta), rho * Math.cos(theta)];
  const arcPts = [];
  const ARC_2D = 40;
  for (let i = 0; i <= ARC_2D; i++) {
    arcPts.push(sectorPt(s, -phi0 / 2 + (phi0 * i) / ARC_2D));
  }
  const apex2d = [0, 0];
  const sectorOutline = [apex2d, ...arcPts];
  const sectorEdges = [
    { seam: "side", pts: [apex2d, arcPts[0]] },
    ...arcPts.slice(0, -1).map((p, i) => ({ seam: "base", pts: [p, arcPts[i + 1]] })),
    { seam: "side", pts: [arcPts[arcPts.length - 1], apex2d] },
  ];
  const lidOutline = circleOutline(r);

  return {
    positionsAt,
    faces,
    seams,
    folds: [],
    pieces: [
      {
        id: "sector",
        name: "sector (the wrap)",
        cut: 1,
        outline: sectorOutline,
        edges: sectorEdges,
        folds2d: [],
        marks: [1, 2, 3].map((k) => {
          const theta = -phi0 / 2 + (phi0 * k) / 4;
          const tick = Math.max(0.6, s * 0.06);
          return [sectorPt(s, theta), sectorPt(s - tick, theta)];
        }),
        notes: [
          `slant s = √(r² + h²) = ${fmt(s)} cm — the sector's radius`,
          `sector angle = 360° · r/s = ${fmt((phi0 / TAU) * 360)}°`,
          `arc length = 2πr = ${fmt(TAU * r)} cm — matches the base circle`,
        ],
      },
      {
        id: "base",
        name: "base circle",
        cut: 1,
        outline: lidOutline,
        edges: outlineEdges(lidOutline, "base"),
        folds2d: [],
        marks: circleMarks(r),
        notes: [
          `radius = ${fmt(r)} cm`,
          "quarter marks match the ticks on the sector's arc",
        ],
      },
    ],
    pieceTones: { sector: [330, 70], base: [200, 75] },
    insight:
      "A cone is a pizza slice of a BIGGER circle — radius √(r² + h²), not r. Taller cone → skinnier slice. At h = 0 the slice becomes a full circle again.",
  };
};

/* ============================================================
   Sphere — there is no flat piece that makes a ball, so we
   cheat with gores: petal-shaped strips that each cover a
   slice of longitude. More gores = rounder ball.
   ============================================================ */

const GORE_ROWS = 10; // latitude steps per gore
const GORE_COLS = 2; // width steps per gore

// `rows` is a hidden tessellation override used by composites.
const buildSphere = ({ radius: r, gores: nRaw, rows: rowsOverride }) => {
  const n = Math.round(nRaw);
  const rows = rowsOverride || GORE_ROWS;
  const cols = GORE_COLS;
  const goreW = (TAU * r) / n; // widest point of one flat gore
  const gapX = Math.max(0.6, r * 0.15);
  const vertsPerGore = (rows + 1) * (cols + 1);

  const goreIdx = (k, row, col) => k * vertsPerGore + row * (cols + 1) + col;

  const positionsAt = (t) => {
    const pts = [];
    const center = [0, r, 0]; // ball rests on the table when sewn
    for (let k = 0; k < n; k++) {
      const lonC = (k + 0.5) * (TAU / n) - Math.PI;
      const xk = (k - (n - 1) / 2) * (goreW + gapX);
      for (let row = 0; row <= rows; row++) {
        const lat = -Math.PI / 2 + (Math.PI * row) / rows;
        for (let col = 0; col <= cols; col++) {
          const u = -1 + (2 * col) / cols;
          const flat = [xk + u * (goreW / 2) * Math.cos(lat), 0, r * lat];
          const lon = lonC + (u * (TAU / n)) / 2;
          const sewn = [
            center[0] + r * Math.cos(lat) * Math.sin(lon),
            center[1] + r * Math.sin(lat),
            center[2] + r * Math.cos(lat) * Math.cos(lon),
          ];
          pts.push(lerp3(flat, sewn, t));
        }
      }
    }
    return pts;
  };

  const faces = [];
  for (let k = 0; k < n; k++) {
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        faces.push({
          verts: [
            goreIdx(k, row, col),
            goreIdx(k, row, col + 1),
            goreIdx(k, row + 1, col + 1),
            goreIdx(k, row + 1, col),
          ],
          piece: "gore",
        });
      }
    }
  }

  const meridianEdges = [];
  for (let k = 0; k < n; k++) {
    for (let row = 0; row < rows; row++) {
      meridianEdges.push([goreIdx(k, row, 0), goreIdx(k, row + 1, 0)]);
      meridianEdges.push([goreIdx(k, row, cols), goreIdx(k, row + 1, cols)]);
    }
  }

  const seams = [
    {
      id: "meridian",
      label: `meridian seams × ${n} — each gore's right edge sews to the next gore's left edge; leave most of the last one open`,
      color: seamColor(0),
      edges: meridianEdges,
      order: 1,
      gap: true,
    },
  ];

  // 2D gore: a petal — width tapers with cos(latitude).
  const SAMP = 24;
  const right = [];
  for (let i = 0; i <= SAMP; i++) {
    const lat = -Math.PI / 2 + (Math.PI * i) / SAMP;
    right.push([(goreW / 2) * Math.cos(lat), r * lat]);
  }
  const left = right
    .slice()
    .reverse()
    .map(([x, y]) => [-x, y]);
  const outline = [...right, ...left.slice(1, -1)];
  const goreEdges = outlineEdges(outline, "meridian");

  return {
    positionsAt,
    faces,
    seams,
    folds: [],
    pieces: [
      {
        id: "gore",
        name: "gore (petal)",
        cut: n,
        outline,
        edges: goreEdges,
        folds2d: [],
        notes: [
          `length pole-to-pole = πr = ${fmt(Math.PI * r)} cm`,
          `widest point = 2πr/${n} = ${fmt(goreW)} cm`,
          `cut ${n} — more gores = rounder ball, more sewing`,
        ],
      },
    ],
    pieceTones: { gore: [260, 65] },
    insight:
      "No flat shape folds into a ball — a sphere has curvature everywhere. Gores approximate it, and the stuffing lies about the rest. Try 4 gores vs 12.",
  };
};

/* ============================================================
   Soft shapes — outline machinery.

   The pancake and the gusset are built from 2D outlines instead
   of closed-form math. The library outlines are coarse polygons
   smoothed with Chaikin corner-cutting, which guarantees gentle,
   sewable curves (and is exactly how a future draw-your-own
   editor can stay sewable: any control polygon works).
   ============================================================ */

// Chaikin corner cutting on a closed polygon: each pass replaces every
// vertex with two points at 1/4 and 3/4 of its edges. Two passes turn a
// dozen-point sketch into a smooth curve.
const chaikin = (pts, passes = 2) => {
  let out = pts;
  for (let p = 0; p < passes; p++) {
    const next = [];
    for (let i = 0; i < out.length; i++) {
      const a = out[i];
      const b = out[(i + 1) % out.length];
      next.push(
        [a[0] * 0.75 + b[0] * 0.25, a[1] * 0.75 + b[1] * 0.25],
        [a[0] * 0.25 + b[0] * 0.75, a[1] * 0.25 + b[1] * 0.75]
      );
    }
    out = next;
  }
  return out;
};

const perimeterOf = (pts) => {
  let len = 0;
  for (let i = 0; i < pts.length; i++) {
    const a = pts[i];
    const b = pts[(i + 1) % pts.length];
    len += Math.hypot(b[0] - a[0], b[1] - a[1]);
  }
  return len;
};

// Resample a closed polyline to n points at uniform arc-length spacing —
// needed so the gusset strip maps onto the profile without distortion and
// quarter registration marks land at true quarter-perimeter.
const resampleClosed = (pts, n) => {
  const total = perimeterOf(pts);
  const out = [];
  let seg = 0; // index of segment start
  let segStart = 0; // arc length at segment start
  let segLen = Math.hypot(
    pts[1 % pts.length][0] - pts[0][0],
    pts[1 % pts.length][1] - pts[0][1]
  );
  for (let k = 0; k < n; k++) {
    const target = (total * k) / n;
    while (segStart + segLen < target) {
      segStart += segLen;
      seg++;
      const a = pts[seg % pts.length];
      const b = pts[(seg + 1) % pts.length];
      segLen = Math.hypot(b[0] - a[0], b[1] - a[1]);
    }
    const a = pts[seg % pts.length];
    const b = pts[(seg + 1) % pts.length];
    const u = segLen === 0 ? 0 : (target - segStart) / segLen;
    out.push([a[0] + (b[0] - a[0]) * u, a[1] + (b[1] - a[1]) * u]);
  }
  return out;
};

const bboxOf = (pts) => {
  const xs = pts.map(([x]) => x);
  const ys = pts.map(([, y]) => y);
  return {
    minX: Math.min(...xs),
    maxX: Math.max(...xs),
    minY: Math.min(...ys),
    maxY: Math.max(...ys),
  };
};

// Scale an outline so its width is exactly `w`, centered on the origin.
const fitOutline = (pts, w) => {
  const b = bboxOf(pts);
  const k = w / (b.maxX - b.minX);
  const cx = (b.minX + b.maxX) / 2;
  const cy = (b.minY + b.maxY) / 2;
  return pts.map(([x, y]) => [(x - cx) * k, (y - cy) * k]);
};

// The outline library: coarse control polygons (y up), smoothed on build.
// Kept deliberately low-poly — these are sketches, the smoothing does the
// rest, and a future outline editor produces exactly this kind of data.
const OUTLINE_LIBRARY = {
  heart: {
    label: "heart",
    coarse: [
      [0, -1.0], [0.55, -0.45], [0.95, 0.1], [0.95, 0.55], [0.6, 0.85],
      [0.25, 0.8], [0, 0.55], [-0.25, 0.8], [-0.6, 0.85], [-0.95, 0.55],
      [-0.95, 0.1], [-0.55, -0.45],
    ],
  },
  star: {
    label: "star",
    coarse: (() => {
      const pts = [];
      for (let i = 0; i < 5; i++) {
        const a = -Math.PI / 2 + (TAU * i) / 5;
        const b = a + TAU / 10;
        pts.push([Math.cos(a), Math.sin(a)], [0.45 * Math.cos(b), 0.45 * Math.sin(b)]);
      }
      return pts;
    })(),
  },
  fish: {
    label: "fish",
    coarse: [
      [-1.0, 0], [-0.7, 0.35], [-0.2, 0.5], [0.3, 0.4], [0.55, 0.15],
      [0.85, 0.4], [1.0, 0.3], [0.9, 0], [1.0, -0.3], [0.85, -0.4],
      [0.55, -0.15], [0.3, -0.4], [-0.2, -0.5], [-0.7, -0.35],
    ],
  },
  blob: {
    label: "blob",
    coarse: (() => {
      const pts = [];
      for (let i = 0; i < 14; i++) {
        const a = (TAU * i) / 14;
        const r = 1 + 0.22 * Math.sin(3 * a + 0.8) + 0.12 * Math.sin(5 * a + 2.1);
        pts.push([r * Math.cos(a), r * Math.sin(a)]);
      }
      return pts;
    })(),
  },
  loaf: {
    label: "loaf",
    coarse: [
      [-1.0, 0], [1.0, 0], [1.0, 0.7], [0.75, 1.05], [0.3, 1.2],
      [-0.3, 1.2], [-0.75, 1.05], [-1.0, 0.7],
    ],
  },
  roundbox: {
    label: "rounded box",
    coarse: [
      [-1.0, -0.35], [-0.85, -0.55], [0.85, -0.55], [1.0, -0.35],
      [1.0, 0.35], [0.85, 0.55], [-0.85, 0.55], [-1.0, 0.35],
    ],
  },
  egg: {
    label: "egg",
    coarse: (() => {
      const pts = [];
      for (let i = 0; i < 16; i++) {
        const a = (TAU * i) / 16;
        // wider at the bottom, tapered on top
        const r = 1 - 0.18 * Math.sin(a);
        pts.push([0.75 * r * Math.cos(a), r * Math.sin(a)]);
      }
      return pts;
    })(),
  },
};

export const outlineChoices = (ids) =>
  ids.map((id) => ({ value: id, label: OUTLINE_LIBRARY[id].label }));

// Smooth + uniform-resample + fit a library outline to a target width.
const buildOutline = (id, width, samples) =>
  fitOutline(resampleClosed(chaikin(OUTLINE_LIBRARY[id].coarse, 2), samples), width);

// Concentric rings toward the centroid — the tessellation that lets a flat
// panel puff. Level 0 is the outline itself; the last "ring" is the center.
const RING_SCALES = [1, 0.66, 0.36];

const ringVerts = (outline) => {
  const n = outline.length;
  const cx = outline.reduce((s, p) => s + p[0], 0) / n;
  const cy = outline.reduce((s, p) => s + p[1], 0) / n;
  const verts = [];
  RING_SCALES.forEach((k) => {
    outline.forEach(([x, y]) => {
      verts.push([cx + (x - cx) * k, cy + (y - cy) * k]);
    });
  });
  verts.push([cx, cy]); // center
  return verts;
};

// Puff fraction per ring level (0 at the seam edge, 1 at the center) with a
// balloon-like circular profile: steep near the seam, flat in the middle.
const ringPuff = RING_SCALES.map((_, i) => {
  const u = i / RING_SCALES.length;
  return Math.sqrt(1 - (1 - u) * (1 - u));
}).concat([1]);

// Faces for one ring-tessellated panel starting at vertex offset `base`.
const ringFaces = (base, n, piece) => {
  const faces = [];
  const L = RING_SCALES.length;
  for (let lvl = 0; lvl < L - 1; lvl++) {
    for (let i = 0; i < n; i++) {
      faces.push({
        verts: [
          base + lvl * n + i,
          base + lvl * n + ((i + 1) % n),
          base + (lvl + 1) * n + ((i + 1) % n),
          base + (lvl + 1) * n + i,
        ],
        piece,
      });
    }
  }
  const center = base + L * n;
  for (let i = 0; i < n; i++) {
    faces.push({
      verts: [base + (L - 1) * n + i, base + (L - 1) * n + ((i + 1) % n), center],
      piece,
    });
  }
  return faces;
};

/* ============================================================
   Pancake — two matching outlines, sewn around the edge and
   stuffed. The simplest organic construction there is: you
   don't draft the third dimension, the stuffing supplies it,
   and thickness follows the outline's local width.
   ============================================================ */

const PANCAKE_SAMPLES = 36;

const buildPancake = ({ outline: outlineId, size, stuffing }) => {
  const n = PANCAKE_SAMPLES;
  const outline = buildOutline(outlineId, size, n);
  const b = bboxOf(outline);
  const amp = stuffing * 0.22 * Math.min(b.maxX - b.minX, b.maxY - b.minY);
  const gap = Math.max(1.5, size * 0.12);
  const rings = ringVerts(outline);
  const L = RING_SCALES.length;
  const vertsPerPanel = rings.length; // n * L + 1
  const halfW = (b.maxX - b.minX) / 2;

  const puffAt = (vi) => ringPuff[Math.min(L, Math.floor(vi / n))];

  const positionsAt = (t) => {
    const pts = [];
    // bottom panel (flat: left of center; sewn: puffs downward, resting on
    // the table, so the whole sandwich floats up by `amp`)
    rings.forEach(([x, y], vi) => {
      const flat = [x - (halfW + gap), 0, -y];
      const sewn = [x, amp * (1 - puffAt(vi)), -y];
      pts.push(lerp3(flat, sewn, t));
    });
    // top panel (flat: mirrored — for an asymmetric outline you cut the
    // second panel flipped; sewn: puffs upward)
    rings.forEach(([x, y], vi) => {
      const flat = [-x + (halfW + gap), 0, -y];
      const sewn = [x, amp * (1 + puffAt(vi)), -y];
      pts.push(lerp3(flat, sewn, t));
    });
    return pts;
  };

  const faces = [...ringFaces(0, n, "panel"), ...ringFaces(vertsPerPanel, n, "panel")];

  const edgeEdges = [];
  for (let i = 0; i < n; i++) {
    edgeEdges.push([i, (i + 1) % n]);
    edgeEdges.push([vertsPerPanel + i, vertsPerPanel + ((i + 1) % n)]);
  }
  const seams = [
    {
      id: "edge",
      label: "the one seam — all the way around the outline; leave a gap for turning",
      color: seamColor(0),
      edges: edgeEdges,
      order: 1,
      gap: true,
    },
  ];

  const pattern = outline.map(([x, y]) => [x, -y]); // pattern coords are y-down

  return {
    positionsAt,
    faces,
    seams,
    folds: [],
    pieces: [
      {
        id: "panel",
        name: `${OUTLINE_LIBRARY[outlineId].label} panel`,
        cut: 2,
        outline: pattern,
        edges: outlineEdges(pattern, "edge"),
        folds2d: [],
        notes: [
          `${fmt(b.maxX - b.minX)} × ${fmt(b.maxY - b.minY)} cm — cut 2 (flip one if the shape isn't symmetric)`,
          `seam length = perimeter = ${fmt(perimeterOf(outline))} cm`,
          "you never draft the thickness — stuffing supplies it, deepest where the outline is widest",
        ],
      },
    ],
    pieceTones: { panel: [330, 65] },
    insight:
      "No drafting at all: any outline becomes a plush. The trade: you don't control thickness — stuffing does, and it puffs the fat middle most while corners stay flat. Scrub the stuffing slider to see the deal you're making.",
  };
};

/* ============================================================
   Profile + gusset — two profile panels and one long strip
   between them. The strip buys the control the pancake lacks
   (depth is now a decision), and carries the deepest rule in
   the curriculum: the strip's length is the profile's
   perimeter. 2πr was never about circles.
   ============================================================ */

const GUSSET_SAMPLES = 40;

const buildGusset = ({ outline: outlineId, size, depth, stuffing }) => {
  const n = GUSSET_SAMPLES;
  const raw = buildOutline(outlineId, size, n);
  const b0 = bboxOf(raw);
  // Stand the profile upright, resting on the table.
  const profile = raw.map(([x, y]) => [x, y - b0.minY]);
  const b = bboxOf(profile);
  const P = perimeterOf(profile);
  const amp = stuffing * 0.16 * Math.min(b.maxX - b.minX, b.maxY - b.minY);
  const gap = Math.max(1.5, depth * 0.35);
  const rings = ringVerts(profile);
  const L = RING_SCALES.length;
  const vertsPerPanel = rings.length;
  const stripBase = vertsPerPanel * 2;

  const puffAt = (vi) => ringPuff[Math.min(L, Math.floor(vi / n))];

  // Cumulative arc length at each sample (uniform by construction).
  const arcAt = (i) => (P * i) / n;

  const positionsAt = (t) => {
    const pts = [];
    // panels: flat on the table on either side of the strip; sewn: vertical
    // at z = ±depth/2, puffing outward with stuffing.
    [-1, 1].forEach((side) => {
      rings.forEach(([x, y], vi) => {
        const flat = [x, 0, side * (depth / 2 + gap + b.maxY / 2) + side * (y - b.maxY / 2)];
        const sewn = [x, y, side * (depth / 2 + amp * puffAt(vi))];
        pts.push(lerp3(flat, sewn, t));
      });
    });
    // strip: a P × depth rectangle that wraps the profile (columns 0 and n
    // coincide when sewn — that's the loop seam).
    for (let i = 0; i <= n; i++) {
      const [px, py] = profile[i % n];
      const s = arcAt(i);
      for (const zSign of [-1, 1]) {
        const flat = [s - P / 2, 0, (zSign * depth) / 2];
        const sewn = [px, py, (zSign * depth) / 2];
        pts.push(lerp3(flat, sewn, t));
      }
    }
    return pts;
  };

  const stripIdx = (col, row) => stripBase + col * 2 + row;
  const faces = [
    ...ringFaces(0, n, "panel"),
    ...ringFaces(vertsPerPanel, n, "panel"),
  ];
  for (let i = 0; i < n; i++) {
    faces.push({
      verts: [stripIdx(i, 0), stripIdx(i + 1, 0), stripIdx(i + 1, 1), stripIdx(i, 1)],
      piece: "strip",
    });
  }

  const rimEdges = [];
  for (let i = 0; i < n; i++) {
    rimEdges.push([i, (i + 1) % n]); // panel A outline
    rimEdges.push([vertsPerPanel + i, vertsPerPanel + ((i + 1) % n)]); // panel B
    rimEdges.push([stripIdx(i, 0), stripIdx(i + 1, 0)]);
    rimEdges.push([stripIdx(i, 1), stripIdx(i + 1, 1)]);
  }
  const seams = [
    {
      id: "loop",
      label: "loop seam — the strip's two short ends meet",
      color: seamColor(0),
      edges: [
        [stripIdx(0, 0), stripIdx(0, 1)],
        [stripIdx(n, 0), stripIdx(n, 1)],
      ],
      order: 1,
    },
    {
      id: "rim",
      label: "rims — each panel sews to one long edge of the strip; leave part of the second open",
      color: seamColor(1),
      edges: rimEdges,
      order: 2,
      gap: true,
    },
  ];

  // 2D pieces (pattern coords y-down). Quarter registration marks on both
  // the strip and the panels, at true quarter-perimeter points.
  const pattern = profile.map(([x, y]) => [x, -y]);
  const quarterIdx = [1, 2, 3].map((k) => Math.round((n * k) / 4));
  const tick = Math.max(0.6, depth * 0.12);
  const panelMarks = quarterIdx.map((i) => {
    const [x, y] = pattern[i];
    const [ix, iy] = [rings[n + i][0], -rings[n + i][1]]; // toward ring 1
    const len = Math.hypot(ix - x, iy - y) || 1;
    return [
      [x, y],
      [x + ((ix - x) / len) * tick, y + ((iy - y) / len) * tick],
    ];
  });
  const stripOutline = [
    [-P / 2, -depth / 2],
    [P / 2, -depth / 2],
    [P / 2, depth / 2],
    [-P / 2, depth / 2],
  ];
  const stripMarks = quarterIdx.flatMap((i) => {
    const x = arcAt(i) - P / 2;
    return [
      [[x, -depth / 2], [x, -depth / 2 + tick]],
      [[x, depth / 2], [x, depth / 2 - tick]],
    ];
  });

  return {
    positionsAt,
    faces,
    seams,
    folds: [],
    pieces: [
      {
        id: "panel",
        name: `${OUTLINE_LIBRARY[outlineId].label} profile`,
        cut: 2,
        outline: pattern,
        edges: outlineEdges(pattern, "rim"),
        folds2d: [],
        marks: panelMarks,
        notes: [
          `${fmt(b.maxX - b.minX)} × ${fmt(b.maxY)} cm`,
          "quarter marks match the ticks on the strip — pin there first, ease between",
        ],
      },
      {
        id: "strip",
        name: "gusset strip",
        cut: 1,
        outline: stripOutline,
        edges: [
          { seam: "rim", pts: [stripOutline[0], stripOutline[1]] },
          { seam: "loop", pts: [stripOutline[1], stripOutline[2]] },
          { seam: "rim", pts: [stripOutline[2], stripOutline[3]] },
          { seam: "loop", pts: [stripOutline[3], stripOutline[0]] },
        ],
        folds2d: [],
        marks: stripMarks,
        notes: [
          `length = profile perimeter = ${fmt(P)} cm — measured, not chosen`,
          `width = depth = ${fmt(depth)} cm — this one IS chosen`,
          "the 2πr rule generalized: any two edges that sew together must be the same length",
        ],
      },
    ],
    pieceTones: { panel: [30, 60], strip: [45, 80] },
    insight:
      "The gusset buys you what the pancake can't: depth is now a decision. The strip's width is yours to choose — its length is not. It's the profile's perimeter, exactly. 2πr was never about circles.",
  };
};

/* ============================================================
   Lathe — the sphere generalized. Spin any silhouette around a
   vertical axis and the gore trick still works: each flat petal
   is as long as the silhouette's ARC and as wide as the local
   circumference ÷ n. The sphere was never special — its
   silhouette just happened to be a semicircle.
   ============================================================ */

// Chaikin for OPEN polylines: endpoints stay pinned, interior corners cut.
const chaikinOpen = (pts, passes = 2) => {
  let out = pts;
  for (let p = 0; p < passes; p++) {
    const next = [out[0]];
    for (let i = 0; i < out.length - 1; i++) {
      const a = out[i];
      const b = out[i + 1];
      next.push(
        [a[0] * 0.75 + b[0] * 0.25, a[1] * 0.75 + b[1] * 0.25],
        [a[0] * 0.25 + b[0] * 0.75, a[1] * 0.25 + b[1] * 0.75]
      );
    }
    next.push(out[out.length - 1]);
    out = next;
  }
  return out;
};

// Resample an open polyline to n+1 points at uniform arc spacing
// (endpoints exact) — the open-curve sibling of resampleClosed.
const resampleOpen = (pts, n) => {
  const cum = [0];
  for (let i = 1; i < pts.length; i++) {
    cum.push(
      cum[i - 1] + Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1])
    );
  }
  const total = cum[cum.length - 1];
  const out = [];
  let seg = 0;
  for (let k = 0; k <= n; k++) {
    const target = (total * k) / n;
    while (seg < pts.length - 2 && cum[seg + 1] < target) seg++;
    const span = cum[seg + 1] - cum[seg];
    const u = span === 0 ? 0 : (target - cum[seg]) / span;
    out.push([
      pts[seg][0] + (pts[seg + 1][0] - pts[seg][0]) * u,
      pts[seg][1] + (pts[seg + 1][1] - pts[seg][1]) * u,
    ]);
  }
  return out;
};

// Silhouette library: coarse open polylines of [radius, y], bottom pole
// first, y in 0..1, radius 0 at both ends so the shape closes at the poles.
// Same philosophy as the outline library: low-poly sketches, Chaikin does
// the rest, and a future profile editor produces exactly this data.
const PROFILE_LIBRARY = {
  ball: {
    label: "ball",
    // dense exact semicircle — the lathe's sanity check against the sphere
    coarse: (() => {
      const pts = [];
      for (let i = 0; i <= 20; i++) {
        const a = (Math.PI * i) / 20;
        pts.push([0.5 * Math.sin(a), 0.5 - 0.5 * Math.cos(a)]);
      }
      return pts;
    })(),
  },
  egg: {
    label: "egg",
    coarse: [
      [0, 0], [0.36, 0.05], [0.5, 0.3], [0.46, 0.55], [0.32, 0.78],
      [0.14, 0.94], [0, 1],
    ],
  },
  teardrop: {
    label: "teardrop",
    coarse: [
      [0, 0], [0.42, 0.06], [0.5, 0.28], [0.4, 0.5], [0.2, 0.72],
      [0.07, 0.9], [0, 1],
    ],
  },
  pear: {
    label: "pear",
    coarse: [
      [0, 0], [0.44, 0.07], [0.5, 0.3], [0.34, 0.5], [0.27, 0.62],
      [0.31, 0.78], [0.22, 0.92], [0, 1],
    ],
  },
  pin: {
    label: "bowling pin",
    coarse: [
      [0, 0], [0.4, 0.05], [0.47, 0.2], [0.34, 0.4], [0.17, 0.55],
      [0.15, 0.68], [0.21, 0.8], [0.17, 0.93], [0, 1],
    ],
  },
};

export const profileChoices = (ids) =>
  ids.map((id) => ({ value: id, label: PROFILE_LIBRARY[id].label }));

// Smooth a silhouette, scale to width (max diameter) and height, resample
// to n+1 rows at uniform arc spacing. Returns rows plus their arc lengths.
const latheRows = (profileId, width, height, n) => {
  const smooth = chaikinOpen(PROFILE_LIBRARY[profileId].coarse, 2);
  const rTop = Math.max(...smooth.map(([r]) => r));
  const yTop = Math.max(...smooth.map(([, y]) => y));
  const scaled = smooth.map(([r, y]) => [(r * width) / (2 * rTop), (y * height) / yTop]);
  const pts = resampleOpen(scaled, n);
  const arcs = [0];
  for (let i = 1; i < pts.length; i++) {
    arcs.push(
      arcs[i - 1] + Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1])
    );
  }
  return {
    pts,
    arcs,
    length: arcs[arcs.length - 1],
    rMax: Math.max(...pts.map(([r]) => r)),
  };
};

// Radius of the sewn lathe surface at height y — the creature generator
// uses this to park limbs exactly on the body's surface. (y is monotonic
// along every library silhouette, so a single sweep finds the bracket.)
export const latheRadiusAt = (profileId, width, height, y) => {
  const { pts } = latheRows(profileId, width, height, 64);
  if (y <= pts[0][1]) return pts[0][0];
  for (let i = 1; i < pts.length; i++) {
    if (y <= pts[i][1]) {
      const [r0, y0] = pts[i - 1];
      const [r1, y1] = pts[i];
      const u = y1 === y0 ? 0 : (y - y0) / (y1 - y0);
      return r0 + (r1 - r0) * u;
    }
  }
  return pts[pts.length - 1][0];
};

const LATHE_ROWS = 12;

// `rows` is a hidden tessellation override used by composites.
const buildLathe = ({ profile: profileId, width, height, gores: nRaw, rows: rowsOverride }) => {
  const n = Math.round(nRaw);
  const rows = rowsOverride || LATHE_ROWS;
  const cols = GORE_COLS;
  const prof = latheRows(profileId, width, height, rows);
  const S = prof.length;
  const goreW = (TAU * prof.rMax) / n; // widest point of one flat gore
  const gapX = Math.max(0.6, width * 0.12);
  const vertsPerGore = (rows + 1) * (cols + 1);

  const goreIdx = (k, row, col) => k * vertsPerGore + row * (cols + 1) + col;

  const positionsAt = (t) => {
    const pts = [];
    for (let k = 0; k < n; k++) {
      const lonC = (k + 0.5) * (TAU / n) - Math.PI;
      const xk = (k - (n - 1) / 2) * (goreW + gapX);
      for (let row = 0; row <= rows; row++) {
        const [r, y] = prof.pts[row];
        const halfW = (Math.PI * r) / n; // local circumference / n, halved
        for (let col = 0; col <= cols; col++) {
          const u = -1 + (2 * col) / cols;
          const flat = [xk + u * halfW, 0, prof.arcs[row] - S / 2];
          const lon = lonC + (u * (TAU / n)) / 2;
          const sewn = [r * Math.sin(lon), y, r * Math.cos(lon)];
          pts.push(lerp3(flat, sewn, t));
        }
      }
    }
    return pts;
  };

  const faces = [];
  for (let k = 0; k < n; k++) {
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        faces.push({
          verts: [
            goreIdx(k, row, col),
            goreIdx(k, row, col + 1),
            goreIdx(k, row + 1, col + 1),
            goreIdx(k, row + 1, col),
          ],
          piece: "gore",
        });
      }
    }
  }

  const meridianEdges = [];
  for (let k = 0; k < n; k++) {
    for (let row = 0; row < rows; row++) {
      meridianEdges.push([goreIdx(k, row, 0), goreIdx(k, row + 1, 0)]);
      meridianEdges.push([goreIdx(k, row, cols), goreIdx(k, row + 1, cols)]);
    }
  }

  const seams = [
    {
      id: "meridian",
      label: `meridian seams × ${n} — each gore's right edge sews to the next gore's left edge; leave most of the last one open`,
      color: seamColor(0),
      edges: meridianEdges,
      order: 1,
      gap: true,
    },
  ];

  // 2D gore: the petal, sampled finer than the 3D mesh. Pattern y-down,
  // fat end of the silhouette at the bottom like the sewn shape.
  const SAMP = 28;
  const samp = latheRows(profileId, width, height, SAMP);
  const right = samp.pts.map(([r], i) => [
    (Math.PI * r) / n,
    samp.length / 2 - samp.arcs[i],
  ]);
  const left = right
    .slice()
    .reverse()
    .map(([x, y]) => [-x, y]);
  const outline = [...right, ...left.slice(1, -1)];

  return {
    positionsAt,
    faces,
    seams,
    folds: [],
    pieces: [
      {
        id: "gore",
        name: `${PROFILE_LIBRARY[profileId].label} gore`,
        cut: n,
        outline,
        edges: outlineEdges(outline, "meridian"),
        folds2d: [],
        notes: [
          `tip-to-tip = silhouette arc = ${fmt(S)} cm — measure along the curve, not the height (${fmt(height)} cm)`,
          `widest point = local circumference ÷ ${n} = ${fmt(goreW)} cm`,
          `cut ${n} — the sphere's petal logic, on any silhouette`,
        ],
      },
    ],
    pieceTones: { gore: [170, 55] },
    insight:
      "The sphere was never special. Draw any side view, spin it: gore length = the silhouette's arc, gore width = the local circumference ÷ n. Egg, pear, bowling pin — every plush body you've ever squeezed is this pattern.",
  };
};

/* ============================================================
   Darts — curvature on demand. Cut a wedge out of flat fabric,
   sew the two cut edges together, and the fabric has no choice
   but to dome. This is the hand-sized version of what gores do
   at full scale, and the shaping tool behind every fitted
   garment and every plush chin.
   ============================================================ */

const DART_RINGS = 10;
const DART_COLS = 6; // angular subdivisions per sector

const buildDarts = ({ radius: R, darts: nRaw, bite: biteDeg, reach }) => {
  const n = Math.round(nRaw);
  const phi = (biteDeg * Math.PI) / 180; // wedge angle removed per dart, at the rim
  const a = R * (1 - reach); // darts run from the rim in to radius a
  const cols = DART_COLS;

  // Ring radii: uniform steps, plus one ring snapped to the dart tips so
  // the 3D mesh creases exactly where the darts end.
  const rhoList = [];
  for (let j = 0; j <= DART_RINGS; j++) rhoList.push((R * j) / DART_RINGS);
  if (a > 0 && !rhoList.some((p) => Math.abs(p - a) < 1e-9)) {
    rhoList.push(a);
    rhoList.sort((p, q) => p - q);
  }
  const rings = rhoList.length - 1;

  // Wedge angle removed per dart at radius rho (0 inside the flat center).
  const gapAt = (rho) => (rho <= a ? 0 : (phi * (rho - a)) / (R - a));

  // Sewn surface of revolution: closing the darts shrinks each ring's
  // circumference, and what the circumference gives up, height takes —
  // fabric doesn't stretch, so ds² = dρ² stays fixed.
  const r3 = rhoList.map((p) => (p * (TAU - n * gapAt(p))) / TAU);
  const desc = [0]; // cumulative drop from the center, ring by ring
  for (let j = 1; j <= rings; j++) {
    const dRho = rhoList[j] - rhoList[j - 1];
    const dR3 = r3[j] - r3[j - 1];
    desc.push(desc[j - 1] + Math.sqrt(Math.max(0, dRho * dRho - dR3 * dR3)));
  }
  const H = desc[rings]; // dome height; rim rests on the table

  const vertsPerSector = (rings + 1) * (cols + 1);
  const idx = (k, j, c) => k * vertsPerSector + j * (cols + 1) + c;

  const positionsAt = (t) => {
    const pts = [];
    for (let k = 0; k < n; k++) {
      for (let j = 0; j <= rings; j++) {
        const rho = rhoList[j];
        const g = gapAt(rho);
        for (let c = 0; c <= cols; c++) {
          const w = c / cols;
          // flat: sectors sit in the disc with the dart wedges open
          const thF = (k + 0.5) * (TAU / n) + (w - 0.5) * (TAU / n - g);
          const flat = [rho * Math.sin(thF), 0, rho * Math.cos(thF)];
          // sewn: the wedges close (sector boundaries meet exactly at
          // multiples of TAU/n by symmetry) and the disc domes up
          const thS = (k + w) * (TAU / n);
          const sewn = [r3[j] * Math.sin(thS), H - desc[j], r3[j] * Math.cos(thS)];
          pts.push(lerp3(flat, sewn, t));
        }
      }
    }
    return pts;
  };

  const faces = [];
  for (let k = 0; k < n; k++) {
    for (let j = 0; j < rings; j++) {
      for (let c = 0; c < cols; c++) {
        faces.push({
          verts: [idx(k, j, c), idx(k, j, c + 1), idx(k, j + 1, c + 1), idx(k, j + 1, c)],
          piece: "disc",
        });
      }
    }
  }

  // Dart seams: sector k's right edge meets sector k+1's left edge, but
  // only along the cut — from the dart tip ring out to the rim.
  const dartEdges = [];
  for (let k = 0; k < n; k++) {
    for (let j = 0; j < rings; j++) {
      if (gapAt(rhoList[j + 1]) > 1e-9) {
        dartEdges.push([idx(k, j, cols), idx(k, j + 1, cols)]);
        dartEdges.push([idx((k + 1) % n, j, 0), idx((k + 1) % n, j + 1, 0)]);
      }
    }
  }
  const seams = [
    {
      id: "dart",
      label: `darts × ${n} — sew each pinch shut, cut edge to cut edge, tip to rim`,
      color: seamColor(0),
      edges: dartEdges,
      order: 1,
      // no turning gap: a dome is open at the rim — nothing to turn through
    },
  ];

  // 2D piece: one disc with the dart wedges drawn. Arc segments are raw
  // edges (no seam — the rim hems or joins the next piece); the V edges
  // carry the dart seam color.
  const M = 6; // arc samples per sector
  const outline = [];
  const edges = [];
  for (let k = 0; k < n; k++) {
    const th0 = k * (TAU / n) + phi / 2;
    const th1 = (k + 1) * (TAU / n) - phi / 2;
    const base = outline.length;
    for (let m = 0; m <= M; m++) {
      const th = th0 + ((th1 - th0) * m) / M;
      outline.push([R * Math.cos(th), R * Math.sin(th)]);
    }
    const thb = (k + 1) * (TAU / n);
    outline.push([a * Math.cos(thb), a * Math.sin(thb)]);
    for (let m = 0; m < M; m++) {
      edges.push({ seam: null, pts: [outline[base + m], outline[base + m + 1]] });
    }
    edges.push({ seam: "dart", pts: [outline[base + M], outline[base + M + 1]] });
  }
  // close each dart V into the next sector's first arc point
  for (let k = 0; k < n; k++) {
    const tip = k * (M + 2) + M + 1;
    const next = ((k + 1) % n) * (M + 2);
    edges.push({ seam: "dart", pts: [outline[tip], outline[next]] });
  }

  return {
    positionsAt,
    faces,
    seams,
    folds: [],
    pieces: [
      {
        id: "disc",
        name: "darted disc",
        cut: 1,
        outline,
        edges,
        folds2d: [],
        notes: [
          `disc radius = ${fmt(R)} cm — that's the dome's slant, not its footprint`,
          `${n} darts × ${fmt(biteDeg)}° = ${fmt(n * biteDeg)}° pinched away → the dome rises ${fmt(H)} cm`,
          "the gray rim stays raw — it hems, or sews onto the next piece",
        ],
      },
    ],
    pieceTones: { disc: [15, 70] },
    insight:
      "A dart is a pinch of nothing: remove a wedge, sew the cut edges together, and flat fabric has no choice but to dome — the circumference you stole becomes height. Every fitted garment is darts in disguise, and gores are just darts that run the whole way.",
  };
};

/* ============================================================
   Shape registry
   ============================================================ */

export const SHAPES = [
  {
    id: "box",
    name: "Box",
    tagline: "six rectangles, one cross",
    params: [
      { key: "width", label: "width", min: 3, max: 20, step: 0.5, def: 12, unit: "cm" },
      { key: "depth", label: "depth", min: 3, max: 20, step: 0.5, def: 8, unit: "cm" },
      { key: "height", label: "height", min: 1, max: 20, step: 0.5, def: 6, unit: "cm" },
    ],
    build: buildBox,
    // Same 3D box, four real-world construction strategies. This is the
    // "different ways people do corners" lesson: the pattern is a choice.
    strategies: [
      {
        id: "net",
        name: "one-piece net",
        build: buildBox,
        blurb: "the paper-craft way: one cross, hinge folds, seven seams",
        tradeoffs: {
          pieces: "1 piece",
          corners: "sewn corners, folded edges (soft unless topstitched)",
          work: "least cutting, but pivoting into inside corners is fiddly",
        },
      },
      {
        id: "strip",
        name: "strip + lids",
        build: buildBoxStrip,
        blurb: "a box is a square cylinder: one wall strip, two lids",
        tradeoffs: {
          pieces: "3 pieces",
          corners: "vertical corners are folds; rim corners are seams",
          work: "one long seam + two set-in lids — the plush-friendly default",
        },
      },
      {
        id: "panels",
        name: "six panels",
        build: buildBoxPanels,
        blurb: "every face its own piece, every edge a seam",
        tradeoffs: {
          pieces: "6 pieces",
          corners: "all seamed — the crispest edges you can get",
          work: "most pinning and sewing; maximum control",
        },
      },
      {
        id: "pillow",
        name: "boxed pillow",
        build: buildBoxPillow,
        blurb: "sew a plain pillow, pinch the corners — no box net at all",
        tradeoffs: {
          pieces: "2 pieces",
          corners: "pinched and sewn across (softly rounded)",
          work: "easiest sewing by far; the box hides in a rectangle",
        },
      },
    ],
  },
  {
    id: "pyramid",
    name: "Pyramid",
    tagline: "triangles that lean in",
    params: [
      { key: "base", label: "base", min: 3, max: 20, step: 0.5, def: 10, unit: "cm" },
      { key: "height", label: "height", min: 1, max: 20, step: 0.5, def: 8, unit: "cm" },
    ],
    build: buildPyramid,
  },
  {
    id: "cylinder",
    name: "Cylinder",
    tagline: "a rectangle rolls up",
    params: [
      { key: "radius", label: "radius", min: 1.5, max: 10, step: 0.5, def: 4, unit: "cm" },
      { key: "height", label: "height", min: 2, max: 20, step: 0.5, def: 10, unit: "cm" },
    ],
    build: buildCylinder,
  },
  {
    id: "cone",
    name: "Cone",
    tagline: "a pizza slice wraps",
    params: [
      { key: "radius", label: "radius", min: 1.5, max: 10, step: 0.5, def: 5, unit: "cm" },
      { key: "height", label: "height", min: 1, max: 20, step: 0.5, def: 9, unit: "cm" },
    ],
    build: buildCone,
  },
  {
    id: "sphere",
    name: "Sphere",
    tagline: "petals around a ball",
    params: [
      { key: "radius", label: "radius", min: 2, max: 10, step: 0.5, def: 5, unit: "cm" },
      { key: "gores", label: "gores", min: 3, max: 12, step: 1, def: 8, unit: "" },
    ],
    build: buildSphere,
  },
  {
    id: "lathe",
    name: "Lathe",
    tagline: "spin a silhouette into gores",
    params: [
      {
        key: "profile",
        label: "silhouette",
        type: "choice",
        def: "egg",
        options: profileChoices(["ball", "egg", "teardrop", "pear", "pin"]),
      },
      { key: "width", label: "width", min: 4, max: 16, step: 0.5, def: 9, unit: "cm" },
      { key: "height", label: "height", min: 4, max: 24, step: 0.5, def: 13, unit: "cm" },
      { key: "gores", label: "gores", min: 4, max: 12, step: 1, def: 8, unit: "" },
    ],
    build: buildLathe,
  },
  {
    id: "dart",
    name: "Darts",
    tagline: "pinch flat fabric into a dome",
    params: [
      { key: "radius", label: "disc radius", min: 4, max: 12, step: 0.5, def: 8, unit: "cm" },
      { key: "darts", label: "darts", min: 2, max: 8, step: 1, def: 4, unit: "" },
      { key: "bite", label: "dart bite", min: 5, max: 30, step: 1, def: 18, unit: "°" },
      { key: "reach", label: "dart reach", min: 0.4, max: 0.95, step: 0.05, def: 0.8, unit: "" },
    ],
    build: buildDarts,
  },
  {
    id: "pancake",
    name: "Pancake",
    tagline: "any outline + stuffing",
    params: [
      {
        key: "outline",
        label: "outline",
        type: "choice",
        def: "fish",
        options: outlineChoices(["fish", "heart", "star", "blob"]),
      },
      { key: "size", label: "width", min: 6, max: 24, step: 0.5, def: 14, unit: "cm" },
      { key: "stuffing", label: "stuffing", min: 0, max: 1, step: 0.05, def: 0.7, unit: "" },
    ],
    build: buildPancake,
  },
  {
    id: "gusset",
    name: "Gusset",
    tagline: "two profiles + a strip",
    params: [
      {
        key: "outline",
        label: "profile",
        type: "choice",
        def: "loaf",
        options: outlineChoices(["loaf", "roundbox", "egg", "heart"]),
      },
      { key: "size", label: "width", min: 6, max: 24, step: 0.5, def: 12, unit: "cm" },
      { key: "depth", label: "depth", min: 2, max: 12, step: 0.5, def: 6, unit: "cm" },
      { key: "stuffing", label: "stuffing", min: 0, max: 1, step: 0.05, def: 0.5, unit: "" },
    ],
    build: buildGusset,
  },
];

export const SHAPE_MAP = Object.fromEntries(SHAPES.map((s) => [s.id, s]));

export const defaultParams = (shape) =>
  Object.fromEntries(shape.params.map((p) => [p.key, p.def]));

export const buildShape = (shapeId, params, strategyId) => {
  const shape = SHAPE_MAP[shapeId];
  const strategy =
    strategyId && shape.strategies
      ? shape.strategies.find((s) => s.id === strategyId)
      : null;
  const build = strategy ? strategy.build : shape.build;
  const model = build({ ...defaultParams(shape), ...params });
  return { shapeId, strategyId: strategy ? strategy.id : null, ...model };
};
